/**
 * 임금 계산 Composable
 * 시급 기반 임금 계산 로직을 제공합니다.
 */
import { HOME_WAGE_NONTAX_TERM } from '@/policies/homeWagePayrollFields';

export interface WageRates {
  base: number;       // 기본급 배율 (기본: 1)
  overtime: number;   // 연장수당 배율 (기본: 1.5)
  night: number;      // 야간수당 배율 (기본: 0.5)
  holiday: number;    // 휴일수당 배율 (기본: 1.5)
}

export interface WageInput {
  hourlyWage: number;
  workHours: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

export interface WageOutput {
  base: number;
  overtime: number;
  night: number;
  holiday: number;
  total: number;
}

const DEFAULT_RATES: WageRates = {
  base: 1,
  overtime: 1.5,
  night: 0.5,
  holiday: 1.5
};

export function useWageCalculator(customRates?: Partial<WageRates>) {
  const rates: WageRates = { ...DEFAULT_RATES, ...customRates };

  /**
   * 기본급 계산: 시급 × 근로시간
   */
  function calcBase(hourlyWage: number, workHours: number): number {
    return Math.round(hourlyWage * rates.base * workHours);
  }

  /**
   * 연장수당 계산: 시급 × 1.5 × 연장시간
   */
  function calcOvertime(hourlyWage: number, overtimeHours: number): number {
    return Math.round(hourlyWage * rates.overtime * overtimeHours);
  }

  /**
   * 야간수당 계산: 시급 × 0.5 × 야간시간
   */
  function calcNight(hourlyWage: number, nightHours: number): number {
    return Math.round(hourlyWage * rates.night * nightHours);
  }

  /**
   * 휴일수당 계산: 시급 × 1.5 × 휴일시간
   */
  function calcHoliday(hourlyWage: number, holidayHours: number): number {
    return Math.round(hourlyWage * rates.holiday * holidayHours);
  }

  /**
   * 전체 임금 계산
   */
  function calcAllWages(input: WageInput): WageOutput {
    const base = calcBase(input.hourlyWage, input.workHours);
    const overtime = calcOvertime(input.hourlyWage, input.overtimeHours);
    const night = calcNight(input.hourlyWage, input.nightHours);
    const holiday = calcHoliday(input.hourlyWage, input.holidayHours);
    
    return {
      base,
      overtime,
      night,
      holiday,
      total: base + overtime + night + holiday
    };
  }

  /**
   * 시급 역산: 기본급과 근로시간으로 시급 계산
   */
  function calcHourlyWageFromBase(base: number, workHours: number): number {
    if (workHours <= 0) return 0;
    return Math.round(base / (rates.base * workHours));
  }

  /**
   * 근로시간 역산: 기본급과 시급으로 근로시간 계산
   */
  function calcWorkHoursFromBase(base: number, hourlyWage: number): number {
    if (hourlyWage <= 0) return 0;
    return Math.round((base / (hourlyWage * rates.base)) * 10) / 10;
  }

  return {
    rates,
    calcBase,
    calcOvertime,
    calcNight,
    calcHoliday,
    calcAllWages,
    calcHourlyWageFromBase,
    calcWorkHoursFromBase
  };
}

/**
 * 공제 요율 인터페이스
 */
export interface DeductionRates {
  rateYeon: number;    // 국민연금 요율 (예: 0.045)
  rateGun: number;     // 건강보험 요율 (예: 0.03545)
  rateJang: number;    // 장기요양 요율 (예: 0.1295, 건강보험료 대비)
  /** 장기요양 산출용 건강보험 요율 — 만 65세+ 건강 면제 시에도 산출 건강보험료 기준으로 장기요양 계산 */
  rateGunForJang?: number;
  rateGo: number;      // 고용보험 요율 (예: 0.009)
  rateSil: number;     // 실업급여 요율 (기관)
  rateSan: number;     // 산재보험 요율 (기관)
}

/**
 * 근로자 공제 계산 결과
 */
export interface EmployeeDeductions {
  yeon: number;       // 국민연금
  gun: number;        // 건강보험
  jang: number;       // 장기요양
  go: number;         // 고용보험
  so: number;         // 소득세 (수동)
  ji: number;         // 지방세 (소득세 × 10%)
  total: number;      // 합계
}

/**
 * 기관 공제 계산 결과
 */
export interface EmployerDeductions {
  yeon: number;       // 국민연금
  gun: number;        // 건강보험
  jang: number;       // 장기요양
  go: number;         // 고용보험
  sil: number;        // 실업급여
  san: number;        // 산재보험
  out: number;        // 퇴직금 (1/12)
  total: number;      // 합계
}

/**
 * 근로자 공제 계산
 * @param taxableWage 과세임금 (통상임금 또는 보수월액)
 * @param rates 공제 요율
 * @param incomeTax 소득세 (간이세액표 기준, 수동 입력)
 */
export function calcEmployeeDeductions(
  taxableWage: number,
  rates: DeductionRates,
  incomeTax: number = 0
): EmployeeDeductions {
  // 원단위 절삭 (10원 미만 버림)
  const truncateToTen = (value: number) => Math.floor(value / 10) * 10;
  
  const yeon = truncateToTen(Math.round(taxableWage * rates.rateYeon));
  const gun = truncateToTen(Math.round(taxableWage * rates.rateGun));
  const gunForJangRate = rates.rateGunForJang ?? rates.rateGun;
  const gunForJang =
    gunForJangRate !== rates.rateGun
      ? truncateToTen(Math.round(taxableWage * gunForJangRate))
      : gun;
  const jang = truncateToTen(Math.round(gunForJang * rates.rateJang));
  const go = truncateToTen(Math.round(taxableWage * rates.rateGo));
  const so = truncateToTen(incomeTax);
  const ji = truncateToTen(Math.round(so * 0.1));
  
  return {
    yeon,
    gun,
    jang,
    go,
    so,
    ji,
    total: yeon + gun + jang + go + so + ji
  };
}

/**
 * 기관 공제 계산
 * @param taxableWage 과세임금
 * @param rates 공제 요율
 */
export function calcEmployerDeductions(
  taxableWage: number,
  rates: DeductionRates
): EmployerDeductions {
  // 원단위 절삭 (10원 미만 버림)
  const truncateToTen = (value: number) => Math.floor(value / 10) * 10;
  
  const yeon = truncateToTen(Math.round(taxableWage * rates.rateYeon));
  const gun = truncateToTen(Math.round(taxableWage * rates.rateGun));
  const gunForJangRate = rates.rateGunForJang ?? rates.rateGun;
  const gunForJang =
    gunForJangRate !== rates.rateGun
      ? truncateToTen(Math.round(taxableWage * gunForJangRate))
      : gun;
  const jang = truncateToTen(Math.round(gunForJang * rates.rateJang));
  const go = truncateToTen(Math.round(taxableWage * rates.rateGo));
  const sil = truncateToTen(Math.round(taxableWage * rates.rateSil));
  const san = truncateToTen(Math.round(taxableWage * rates.rateSan));
  const out = truncateToTen(Math.round(taxableWage / 12));  // 퇴직금도 원단위 절삭 적용
  
  return {
    yeon,
    gun,
    jang,
    go,
    sil,
    san,
    out,
    total: yeon + gun + jang + go + sil + san + out
  };
}

/**
 * 숫자 포맷팅 유틸리티
 */
export function formatWageNumber(num: number | null | undefined): string {
  if (num == null) return '0';
  return num.toLocaleString();
}

/**
 * 숫자 파싱 유틸리티 (콤마 포함 문자열 -> 숫자)
 */
export function parseWageNumber(val: string | number | null | undefined): number {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  return parseInt(String(val).replace(/[^\d-]/g, ''), 10) || 0;
}

/**
 * 시간 입력 문자열 정규화 (소수점 1자리까지, 예: 3.5)
 */
export function sanitizeHoursInput(raw: string): string {
  let cleaned = String(raw).replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 1) {
    cleaned = `${parts[0]}.${parts.slice(1).join('').slice(0, 1)}`;
  }
  return cleaned;
}

/**
 * 시간 표시 (0이면 빈 문자열, 정수는 소수점 없음)
 * DB에서 문자열로 올 수 있으므로 숫자로 변환 후 처리
 */
export function formatHoursDisplay(hours: number | string | null | undefined): string {
  if (hours == null) return '';
  const num = typeof hours === 'number' ? hours : parseFloat(String(hours));
  if (!Number.isFinite(num) || num <= 0) return '';
  const rounded = Math.round(num * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * 시간(소수) → 「n시간 n분」 / 「n분」 (수가시간·근무시간 표시용)
 * 예: 40/60h → 40분, 2.5h → 2시간 30분, 1h → 1시간
 */
export function formatDurationHoursMinutes(hours: number | string | null | undefined): string {
  if (hours == null) return '';
  const num = typeof hours === 'number' ? hours : parseFloat(String(hours));
  if (!Number.isFinite(num) || num <= 0) return '';
  const totalMinutes = Math.round(num * 60);
  if (totalMinutes <= 0) return '';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}시간 ${m}분`;
  if (h > 0) return `${h}시간`;
  return `${m}분`;
}

/**
 * blur 시 시간 입력값 정규화
 */
export function normalizeHoursInputValue(raw: string): string {
  const parsed = parseHours(raw);
  return parsed > 0 ? formatHoursDisplay(parsed) : '';
}

/**
 * 시간 파싱 유틸리티 (소수점 1자리까지)
 */
export function parseHours(val: string | number | null | undefined): number {
  if (val == null) return 0;
  const n = typeof val === 'number'
    ? val
    : parseFloat(String(val).replace(/,/g, '').trim());
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10) / 10;
}

/**
 * 연장/야간/휴일수당 비과세 한도 (월간)
 * 생산직근로자 비과세 정책: 연장/야간/휴일 수당에 대해 월 20만원까지 비과세
 * - 적용 대상: 생산직근로자 (체크박스로 설정)
 * - 체크 시: 비과세 계산 로직 적용 (연장→야간→휴일 순서로 20만원 한도 적용)
 * - 해제 시: 모든 연장/야간/휴일 수당이 과세 처리됨
 */
export const OVERTIME_NONTAX_MONTHLY_LIMIT = 200000; // 월 20만원
export const OVERTIME_NONTAX_ANNUAL_LIMIT = 200000; // 월 20만원 (하위호환용, OVERTIME_NONTAX_MONTHLY_LIMIT 사용 권장)

/**
 * 나이 기반 사회보험 면제 기준
 * 
 * [국민연금]
 * - 만 60세 이상: 국민연금 가입 대상에서 제외 (단, 임의계속가입 선택 가능)
 * - 만 18세 미만: 가입 대상에서 제외
 * 
 * [고용보험]
 * - 만 65세 이상: 고용보험 면제 (입사일 무관, 명세서·대장과 동일)
 */
export const AGE_PENSION_EXEMPT = 60; // 국민연금 면제 나이 (만 60세 이상)
/** @deprecated 건강보험은 나이와 무관하게 과세임금 기준 계산 — 면제 로직 미사용 */
export const AGE_HEALTH_EXEMPT = 65;
export const AGE_EMPLOYMENT_EXEMPT = 65; // 고용보험 면제 나이 (만 65세 이상)

/**
 * 나이 기반 사회보험 적용 검증 결과
 */
export interface AgeInsuranceValidation {
  birthDate: string | null;
  age: number | null;
  referenceDate: Date;
  pensionExempt: boolean;      // 국민연금 면제 대상 여부 (만 60세 이상)
  /** @deprecated 항상 false — 건강보험은 나이와 무관하게 계산 */
  healthExempt: boolean;
  employmentExempt: boolean;   // 고용보험 면제 대상 여부 (만 65세 이상)
  pensionMessage: string;
  healthMessage: string;
  employmentMessage: string;
}

/**
 * 만 나이 계산
 * 2023년 6월 28일부터 시행된 만 나이 통일법 기준
 * 
 * @param birthDate 생년월일 (YYYY-MM-DD 형식)
 * @param referenceDate 기준일 (기본값: 현재 날짜)
 * @returns 만 나이 (생년월일이 없으면 null)
 */
export function calcAge(birthDate: string | null | undefined, referenceDate?: Date): number | null {
  if (!birthDate) return null;
  
  const ref = referenceDate || new Date();
  const birth = new Date(birthDate);
  
  if (isNaN(birth.getTime())) return null;
  
  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  
  // 아직 생일이 지나지 않았으면 1세 빼기
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * 특정 연월 기준 만 나이 계산
 * 
 * @param birthDate 생년월일 (YYYY-MM-DD 형식)
 * @param year 기준 연도
 * @param month 기준 월 (1-12)
 * @returns 만 나이
 */
export function calcAgeAtMonth(birthDate: string | null | undefined, year: number, month: number): number | null {
  if (!birthDate) return null;
  
  // 해당 월의 마지막 날 기준으로 계산
  const lastDay = new Date(year, month, 0);
  return calcAge(birthDate, lastDay);
}

/**
 * 나이 기반 사회보험 적용 여부 검증
 * 
 * @param birthDate 생년월일 (YYYY-MM-DD 형식)
 * @param year 기준 연도
 * @param month 기준 월 (1-12)
 */
export function validateAgeInsurance(
  birthDate: string | null | undefined,
  year: number,
  month: number
): AgeInsuranceValidation {
  const referenceDate = new Date(year, month, 0); // 해당 월 말일
  const age = calcAgeAtMonth(birthDate, year, month);
  
  const pensionExempt = age !== null && age >= AGE_PENSION_EXEMPT;
  const healthExempt = false;
  const employmentExempt = age !== null && age >= AGE_EMPLOYMENT_EXEMPT;
  
  let pensionMessage = '';
  if (pensionExempt) {
    pensionMessage = `만 ${age}세로 국민연금 가입 대상 제외 (만 ${AGE_PENSION_EXEMPT}세 이상)`;
  }
  
  const healthMessage = '';
  
  let employmentMessage = '';
  if (employmentExempt) {
    employmentMessage = `만 ${age}세로 고용보험 적용 제외 (만 ${AGE_EMPLOYMENT_EXEMPT}세 이상, 실업급여는 요율 적용)`;
  }
  
  return {
    birthDate: birthDate || null,
    age,
    referenceDate,
    pensionExempt,
    healthExempt,
    employmentExempt,
    pensionMessage,
    healthMessage,
    employmentMessage
  };
}

/**
 * 생년월일 포맷팅 (YYYY-MM-DD → YYYY.MM.DD)
 */
export function formatBirthDate(birthDate: string | null | undefined): string {
  if (!birthDate) return '-';
  return birthDate.replace(/-/g, '.');
}

/**
 * 생산직근로자 비과세 적용 조건
 * 
 * [적용 조건]
 * 1. 직종이 요양보호사, 조리사, 조리원 중 하나여야 함
 * 2. 통상임금이 월 260만원 이하여야 함
 * 
 * [제한 사항]
 * - 통상임금 260만원 초과 시 해당 월에는 생산직근로자 비과세 적용 불가
 * - 비과세 한도: 월 20만원 (연장→야간→휴일 순서로 적용)
 */
export const PRODUCTION_WORKER_USUAL_LIMIT = 2600000; // 월 통상임금 한도 260만원

/**
 * 생산직근로자 비과세 적용 가능 직종
 */
export const PRODUCTION_WORKER_POSITIONS = ['요양보호사', '조리사', '조리원'] as const;

/**
 * 생산직근로자 비과세 검증 결과
 */
export interface ProductionWorkerNontaxValidation {
  isEligiblePosition: boolean;   // 적용 가능 직종 여부
  isUsualWithinLimit: boolean;   // 통상임금 한도 이내 여부
  canApply: boolean;             // 비과세 적용 가능 여부
  usualWage: number;             // 통상임금
  usualLimit: number;            // 통상임금 한도
  positionMessage: string;       // 직종 관련 메시지
  usualMessage: string;          // 통상임금 관련 메시지
}

/**
 * 생산직근로자 비과세 적용 가능 직종인지 확인
 * @param position 직종명
 */
export function isProductionWorkerPosition(position: string | null | undefined): boolean {
  if (!position) return false;
  return PRODUCTION_WORKER_POSITIONS.some(p => position.includes(p));
}

/**
 * 생산직근로자 비과세 적용 가능 여부 검증
 * @param position 직종명
 * @param usualWage 통상임금
 */
export function validateProductionWorkerNontax(
  position: string | null | undefined,
  usualWage: number
): ProductionWorkerNontaxValidation {
  const isEligiblePosition = isProductionWorkerPosition(position);
  const isUsualWithinLimit = usualWage <= PRODUCTION_WORKER_USUAL_LIMIT;
  const canApply = isEligiblePosition && isUsualWithinLimit;
  
  let positionMessage = '';
  if (!isEligiblePosition) {
    positionMessage = '생산직근로자 비과세는 요양보호사, 조리사, 조리원만 적용 가능합니다.';
  }
  
  let usualMessage = '';
  if (!isUsualWithinLimit) {
    const overAmount = usualWage - PRODUCTION_WORKER_USUAL_LIMIT;
    usualMessage = `통상임금이 ${(PRODUCTION_WORKER_USUAL_LIMIT / 10000).toLocaleString()}만원을 ${(overAmount).toLocaleString()}원 초과하여 이번 달 비과세 적용이 불가합니다.`;
  }
  
  return {
    isEligiblePosition,
    isUsualWithinLimit,
    canApply,
    usualWage,
    usualLimit: PRODUCTION_WORKER_USUAL_LIMIT,
    positionMessage,
    usualMessage
  };
}

/**
 * 중식대 월 비과세 한도
 * 소득세법에 따른 중식대 비과세 한도 (월 20만원)
 */
export const LUNCH_MONTHLY_LIMIT = 200000; // 월 20만원

/** 식대·차량보조비·자녀교육비 공통 월 입력 한도 */
export const NONTAX_CASH_MONTHLY_LIMIT = LUNCH_MONTHLY_LIMIT;

export const NONTAX_CASH_HOME_FIELD_KEYS = ['lunch', 'car', 'child'] as const;
export const NONTAX_CASH_CWHD_FIELD_KEYS = ['cwhd_lunch', 'cwhd_car', 'cwhd_child'] as const;

export type NontaxCashHomeFieldKey = (typeof NONTAX_CASH_HOME_FIELD_KEYS)[number];
export type NontaxCashCwhdFieldKey = (typeof NONTAX_CASH_CWHD_FIELD_KEYS)[number];

export function isNontaxCashInputField(field: string): boolean {
  return (
    (NONTAX_CASH_HOME_FIELD_KEYS as readonly string[]).includes(field)
    || (NONTAX_CASH_CWHD_FIELD_KEYS as readonly string[]).includes(field)
  );
}

export function getNontaxCashFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    lunch: HOME_WAGE_NONTAX_TERM.lunch,
    car: HOME_WAGE_NONTAX_TERM.car,
    child: HOME_WAGE_NONTAX_TERM.child,
    cwhd_lunch: HOME_WAGE_NONTAX_TERM.lunch,
    cwhd_car: HOME_WAGE_NONTAX_TERM.car,
    cwhd_child: HOME_WAGE_NONTAX_TERM.child,
  };
  return labels[field] || '비과세 수당';
}

/** 식대·차량·자녀 — 월 20만원 한도 클램프 */
export function clampNontaxCashAllowance(
  amount: number,
  limit = NONTAX_CASH_MONTHLY_LIMIT
): number {
  return Math.min(Math.max(0, Number(amount) || 0), limit);
}

/** 입력 필드별 한도 적용 (초과 시 alert, 방문요양·요양시설 공통) */
export function clampNontaxCashFieldInput(
  field: string,
  amount: number,
  options?: { silent?: boolean }
): number {
  if (!isNontaxCashInputField(field)) {
    return Number(amount) || 0;
  }
  const num = Number(amount) || 0;
  if (num <= NONTAX_CASH_MONTHLY_LIMIT) return num;
  if (!options?.silent) {
    alert(`${getNontaxCashFieldLabel(field)}는 월 20만원까지만 입력 가능합니다.`);
  }
  return NONTAX_CASH_MONTHLY_LIMIT;
}

/**
 * 중식대 실비변상 처리 규칙
 * 
 * [실비변상적 급여란?]
 * - 매월 다른 금액으로 지급되는 경우 실비변상적 성격으로 인정
 * - 통상임금에서 제외되어 퇴직금 계산 시 포함되지 않음
 * 
 * [체크박스 동작]
 * - 체크 ON: 이전 달과 다른 금액으로 설정해야 함
 *   → 실비변상적 급여로 인정 → 통상임금 제외 → 퇴직금 제외
 * - 체크 OFF: 이전 달과 동일한 금액 허용
 *   → 고정적 급여로 인정 → 통상임금 포함 → 퇴직금 포함
 * 
 * [유효성 검사]
 * - 체크 ON 상태에서 이전 달과 동일한 금액이면 경고 표시
 * - 저장 시 유효성 검사 실패 처리
 */
export interface LunchExpenseValidation {
  isValid: boolean;
  currentAmount: number;
  prevMonthAmount: number | null;
  message: string;
}

/**
 * 중식대 실비변상 유효성 검사
 * @param currentAmount 현재 중식대 금액
 * @param _prevMonthAmount 이전 달 중식대 (호환용, 동일 금액 경고는 사용하지 않음)
 * @param _lunchExpenseEnabled 실비변상 처리 체크 여부 (호환용)
 */
export function validateLunchExpense(
  currentAmount: number,
  _prevMonthAmount: number | null = null,
  _lunchExpenseEnabled: boolean = false
): LunchExpenseValidation {
  // 한도 초과 검사만 유지 (전월과 동일 금액 경고는 제거)
  if (currentAmount > LUNCH_MONTHLY_LIMIT) {
    return {
      isValid: false,
      currentAmount,
      prevMonthAmount: _prevMonthAmount,
      message: `중식대는 월 ${(LUNCH_MONTHLY_LIMIT / 10000).toLocaleString()}만원까지만 비과세됩니다.`
    };
  }

  return {
    isValid: true,
    currentAmount,
    prevMonthAmount: _prevMonthAmount,
    message: ''
  };
}

/**
 * 비과세 항목 정의
 * 과세임금(taxable) = 임금합계 - 비과세항목 합계
 */
export const NONTAX_ITEMS = {
  lunch: { key: 'lunch', label: '중식비', dbField: 'cwd_lunch', description: '중식대 비과세 (월 20만원 한도)' },
  car: { key: 'car', label: '차량유지비', dbField: 'cwd_car', description: '자가운전보조금 비과세 (월 20만원 한도)' },
  childcare: { key: 'childcare', label: '양육비', dbField: 'cwd_childcare', description: '육아수당 비과세' },
  overtime_nontax: { key: 'overtime_nontax', label: '연장수당 비과세', dbField: 'cwd_overtime_nontax', description: '연장근로수당 중 비과세 (월 20만원 한도, 연장→야간→휴일 순)' },
  night_nontax: { key: 'night_nontax', label: '야간수당 비과세', dbField: 'cwd_night_nontax', description: '야간근로수당 중 비과세 (월 20만원 한도, 연장→야간→휴일 순)' },
  holiday_nontax: { key: 'holiday_nontax', label: '휴일수당 비과세', dbField: 'cwd_holiday_nontax', description: '휴일근로수당 중 비과세 (월 20만원 한도, 연장→야간→휴일 순)' }
} as const;

export type NontaxItemKey = keyof typeof NONTAX_ITEMS;

/**
 * 비과세로 인한 절감액 계산 결과
 */
export interface NontaxSavingsResult {
  nontaxAmount: number;        // 비과세 금액
  // 근로자 절감
  employeeSavings: {
    yeon: number;              // 국민연금 절감
    gun: number;               // 건강보험 절감
    jang: number;              // 장기요양 절감
    go: number;                // 고용보험 절감
    total: number;             // 근로자 총 절감액
  };
  // 기관 절감
  employerSavings: {
    yeon: number;              // 국민연금 절감
    gun: number;               // 건강보험 절감
    jang: number;              // 장기요양 절감
    go: number;                // 고용보험 절감
    sil: number;               // 실업급여 절감
    san: number;               // 산재보험 절감
    insuranceTotal: number;    // 기관 4대보험 절감 합계
    out: number;               // 퇴직금 절감
    total: number;             // 기관 총 절감액
  };
  totalSavings: number;        // 근로자 + 기관 총 절감액
}

/**
 * 비과세로 인한 절감액 계산
 * 
 * @param nontaxAmount 비과세 금액
 * @param rates 공제 요율
 * @returns 절감액 상세
 */
export function calcNontaxSavings(
  nontaxAmount: number,
  rates: DeductionRates
): NontaxSavingsResult {
  if (nontaxAmount <= 0) {
    return {
      nontaxAmount: 0,
      employeeSavings: { yeon: 0, gun: 0, jang: 0, go: 0, total: 0 },
      employerSavings: { yeon: 0, gun: 0, jang: 0, go: 0, sil: 0, san: 0, insuranceTotal: 0, out: 0, total: 0 },
      totalSavings: 0
    };
  }

  // 근로자 절감액 (4대보험 본인부담분)
  const empYeon = Math.round(nontaxAmount * rates.rateYeon);
  const empGun = Math.round(nontaxAmount * rates.rateGun);
  const empJang = Math.round(empGun * rates.rateJang);
  const empGo = Math.round(nontaxAmount * rates.rateGo);
  const empTotal = empYeon + empGun + empJang + empGo;

  // 기관 절감액 (4대보험 사업주부담분 + 퇴직금)
  const corpYeon = Math.round(nontaxAmount * rates.rateYeon);
  const corpGun = Math.round(nontaxAmount * rates.rateGun);
  const corpJang = Math.round(corpGun * rates.rateJang);
  const corpGo = Math.round(nontaxAmount * rates.rateGo);
  const corpSil = Math.round(nontaxAmount * rates.rateSil);
  const corpSan = Math.round(nontaxAmount * rates.rateSan);
  const corpInsuranceTotal = corpYeon + corpGun + corpJang + corpGo + corpSil + corpSan;
  const corpOut = Math.round(nontaxAmount / 12); // 퇴직금 1/12
  const corpTotal = corpInsuranceTotal + corpOut;

  return {
    nontaxAmount,
    employeeSavings: {
      yeon: empYeon,
      gun: empGun,
      jang: empJang,
      go: empGo,
      total: empTotal
    },
    employerSavings: {
      yeon: corpYeon,
      gun: corpGun,
      jang: corpJang,
      go: corpGo,
      sil: corpSil,
      san: corpSan,
      insuranceTotal: corpInsuranceTotal,
      out: corpOut,
      total: corpTotal
    },
    totalSavings: empTotal + corpTotal
  };
}

/**
 * 비과세 항목 합계 계산
 */
export function calcTotalNontax(values: {
  lunch?: number;
  car?: number;
  childcare?: number;
  dang?: number;
  overtime_nontax?: number;
  night_nontax?: number;
  holiday_nontax?: number;
}): number {
  return (values.lunch || 0) + 
         (values.car || 0) + 
         (values.childcare || 0) + 
         (values.dang || 0) +
         (values.overtime_nontax || 0) + 
         (values.night_nontax || 0) + 
         (values.holiday_nontax || 0);
}

/**
 * 인건비절감액(c_save) 산정 기준 비과세액
 * 방문요양·요양시설·공통 임금설정 공통:
 * = 비과세 합계 − 차량비(car) − 자녀교육비(child)
 */
export function calcWageCenterSavingsNontaxBase(
  totalNontax: number,
  car = 0,
  child = 0
): number {
  const carAmt = Number(car) || 0;
  const childAmt = Number(child) || 0;
  return Math.max(0, (Number(totalNontax) || 0) - carAmt - childAmt);
}

/**
 * 통상임금 계산 입력값
 */
export interface UsualWageInput {
  base: number;        // 기본급
  bonus?: number;      // 상여금
  edu?: number;        // 교육수당
  bojon?: number;      // 보존수당
  etc?: number;        // 기타수당
  dang?: number;       // 당직수당
  lunch?: number;      // 중식비
  car?: number;        // 차량비
  childcare?: number;  // 양육비
}

/**
 * 통상임금 계산
 * 
 * 포함: 기본급, 상여금, 교육수당, 보존수당, 기타수당, 당직수당, 중식비, 차량비, 양육비
 * 제외: 연차수당(법정), 연장/야간/휴일수당(법정), 장기근속/선임수당(국가지원금)
 */
export function calcUsualWage(input: UsualWageInput): number {
  return (input.base || 0) +
         (input.bonus || 0) +
         (input.edu || 0) +
         (input.bojon || 0) +
         (input.etc || 0) +
         (input.dang || 0) +
         (input.lunch || 0) +
         (input.car || 0) +
         (input.childcare || 0);
}

/**
 * 통상임금 산출 내역
 */
export interface UsualWageBreakdown {
  base: number;
  bonus: number;
  edu: number;
  bojon: number;
  etc: number;
  dang: number;
  lunch: number;
  car: number;
  childcare: number;
  total: number;
  // 제외 항목 (참고용)
  excluded: {
    dayoff: number;      // 연차수당
    overtime: number;    // 연장수당
    night: number;       // 야간수당
    holiday: number;     // 휴일수당
    longterm: number;    // 장기근속수당
    sunim: number;       // 선임수당
  };
}

/**
 * 지원공제 계산 입력
 * 국가 지원금(장기근속수당, 선임수당)에 대한 기관 부담금을 근로자 공제로 처리
 * 
 * 중요 규칙:
 * - 근로자공제에서 국민연금이 0으로 설정되면 → 기관공제 국민연금도 0, 지원공제의 국민연금도 0
 * - 근로자공제에서 고용보험이 0으로 설정되면 → 기관공제 고용보험도 0, 지원공제의 고용보험도 0
 * - 실업급여는 고용보험 면제와 별개로 요율대로 계산
 */
export interface SupportDeductionInput {
  longterm: number;      // 장기근속수당
  sunim: number;         // 선임수당
  workHours: number;     // 월 근무시간 (60시간 미만이면 퇴직금 제외)
  yeonDeduct?: boolean;  // 국민연금 공제 적용 여부 (false면 국민연금 0원)
  goDeduct?: boolean;    // 고용보험 공제 적용 여부 (false면 고용보험 0원, 실업급여는 요율 유지)
}

/**
 * 지원공제 계산 결과
 */
export interface SupportDeductionResult {
  baseAmount: number;        // 지원금 기준 금액 (장기근속 + 선임)
  // 4대보험 부분
  insurance: {
    yeon: number;           // 국민연금
    gun: number;            // 건강보험
    jang: number;           // 장기요양
    go: number;             // 고용보험
    sil: number;            // 실업급여
    san: number;            // 산재보험
    total: number;          // 보험 합계
  };
  retirement: number;        // 퇴직금 부분 (baseAmount / 12)
  retirementApplied: boolean; // 퇴직금 적용 여부 (60시간 이상)
  total: number;             // 지원공제 총액
}

/**
 * 지원공제 계산
 * 
 * 공식: (장기근속수당 + 선임수당) × 4대보험율 + (장기근속수당 + 선임수당) ÷ 12
 * - 4대보험: 국민연금, 건강보험, 장기요양, 고용보험, 실업급여, 산재보험
 * - 퇴직금: 근무시간이 60시간 이상인 경우에만 적용
 * - 각 항목 원단위 절삭
 * 
 * 중요: 근로자공제에서 특정 항목이 0으로 설정되면 지원공제에서도 0으로 계산
 * - yeonDeduct가 false면 국민연금 0원
 * - goDeduct가 false면 고용보험 0원 (실업급여는 요율 적용)
 * 
 * @param input 지원공제 입력값
 * @param rates 공제 요율
 */
export function calcSupportDeduction(
  input: SupportDeductionInput,
  rates: DeductionRates
): SupportDeductionResult {
  const baseAmount = (input.longterm || 0) + (input.sunim || 0);
  
  // 공제 적용 여부 (기본값: true)
  const yeonDeduct = input.yeonDeduct !== false;
  const goDeduct = input.goDeduct !== false;
  
  if (baseAmount <= 0) {
    return {
      baseAmount: 0,
      insurance: { yeon: 0, gun: 0, jang: 0, go: 0, sil: 0, san: 0, total: 0 },
      retirement: 0,
      retirementApplied: false,
      total: 0
    };
  }
  
  // 원단위 절삭 함수
  const truncate = (val: number) => Math.floor(val / 10) * 10;
  
  // 4대보험 계산 (각 항목 원단위 절삭)
  // 근로자공제에서 해당 항목이 0이면 지원공제에서도 0으로 계산
  const yeon = yeonDeduct ? truncate(baseAmount * rates.rateYeon) : 0;
  const gun = truncate(baseAmount * rates.rateGun);
  const jang = truncate(gun * rates.rateJang);  // 장기요양은 건강보험 기준
  const go = goDeduct ? truncate(baseAmount * rates.rateGo) : 0;
  const sil = truncate(baseAmount * rates.rateSil);
  const san = truncate(baseAmount * rates.rateSan);
  const insuranceTotal = yeon + gun + jang + go + sil + san;
  
  // 퇴직금 부분 (60시간 이상 근무 시에만)
  const retirementApplied = input.workHours >= 60;
  const retirement = retirementApplied ? truncate(baseAmount / 12) : 0;
  
  return {
    baseAmount,
    insurance: {
      yeon,
      gun,
      jang,
      go,
      sil,
      san,
      total: insuranceTotal
    },
    retirement,
    retirementApplied,
    total: insuranceTotal + retirement
  };
}

/**
 * 연장/야간/휴일수당 비과세 계산 입력
 */
export interface OvertimeNontaxInput {
  overtime: number;        // 당월 연장수당
  night: number;           // 당월 야간수당
  holiday: number;         // 당월 휴일수당
  yearlyAccumulated: number; // 당월 이전까지 연간 누적 비과세 금액
}

/**
 * 연장/야간/휴일수당 비과세 계산 결과
 */
export interface OvertimeNontaxResult {
  overtimeNontax: number;   // 연장수당 비과세
  overtimeTax: number;      // 연장수당 과세
  nightNontax: number;      // 야간수당 비과세
  nightTax: number;         // 야간수당 과세
  holidayNontax: number;    // 휴일수당 비과세
  holidayTax: number;       // 휴일수당 과세
  totalNontax: number;      // 당월 총 비과세
  totalTax: number;         // 당월 총 과세
  newAccumulated: number;   // 당월 포함 새 누적 비과세
  limitExceeded: boolean;   // 한도 초과 여부
}

/**
 * 연장/야간/휴일수당 비과세 계산
 * 월 20만원 한도 내에서 연장→야간→휴일 순서로 비과세 적용하고, 초과분은 과세 처리
 * 
 * @param input 비과세 계산 입력
 * @param monthlyLimit 월 비과세 한도 (기본: 20만원)
 */
export function calcOvertimeNontax(
  input: OvertimeNontaxInput,
  monthlyLimit: number = OVERTIME_NONTAX_MONTHLY_LIMIT
): OvertimeNontaxResult {
  const { overtime, night, holiday } = input;
  // yearlyAccumulated는 더 이상 사용하지 않음 (월 20만원 한도로 변경)
  
  // 당월 총 수당
  const monthlyTotal = overtime + night + holiday;
  
  // 월 한도
  const remainingLimit = monthlyLimit;
  
  // 한도 초과 여부
  const limitExceeded = monthlyTotal > remainingLimit;
  
  // 당월 비과세 가능 총액
  const monthlyNontaxTotal = Math.min(monthlyTotal, remainingLimit);
  
  // 당월 과세 총액
  const monthlyTaxTotal = monthlyTotal - monthlyNontaxTotal;
  
  // 비과세/과세 분배
  // 연장 → 야간 → 휴일 순서로 비과세 적용
  let remainingNontax = monthlyNontaxTotal;
  
  // 연장수당 비과세/과세 분리
  const overtimeNontax = Math.min(overtime, remainingNontax);
  const overtimeTax = overtime - overtimeNontax;
  remainingNontax -= overtimeNontax;
  
  // 야간수당 비과세/과세 분리
  const nightNontax = Math.min(night, remainingNontax);
  const nightTax = night - nightNontax;
  remainingNontax -= nightNontax;
  
  // 휴일수당 비과세/과세 분리
  const holidayNontax = Math.min(holiday, remainingNontax);
  const holidayTax = holiday - holidayNontax;
  
  return {
    overtimeNontax,
    overtimeTax,
    nightNontax,
    nightTax,
    holidayNontax,
    holidayTax,
    totalNontax: monthlyNontaxTotal,
    totalTax: monthlyTaxTotal,
    newAccumulated: (input.yearlyAccumulated || 0) + monthlyNontaxTotal,
    limitExceeded
  };
}
